import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Award, X, Check, Trophy, Palette, Laugh, Newspaper, Mic, Sparkles, BookOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Award category configurations with icons
const AWARD_CATEGORIES = {
  artistic: {
    name: "Artistic Creativity Award",
    description: "Outstanding artistic and creative content",
    emoji: "🎨",
    icon: Palette,
    color: "#8b5cf6"
  },
  comedic: {
    name: "Comedic Award",
    description: "Funniest and most entertaining",
    emoji: "😂",
    icon: Laugh,
    color: "#f59e0b"
  },
  journalistic: {
    name: "Journalistic Award",
    description: "Excellence in news and reporting",
    emoji: "📰",
    icon: Newspaper,
    color: "#3b82f6"
  },
  podcast: {
    name: "Podcast Award",
    description: "Outstanding audio content",
    emoji: "🎙️",
    icon: Mic,
    color: "#ef4444"
  },
  inspirational: {
    name: "Inspirational Award",
    description: "Inspiring and motivating content",
    emoji: "✨",
    icon: Sparkles,
    color: "#10b981"
  },
  educational: {
    name: "Educational Award",
    description: "Best educational content",
    emoji: "📚",
    icon: BookOpen,
    color: "#06b6d4"
  }
};

const VoteForButton = ({ 
  contentId, 
  contentType = "post", // "post", "video", "podcast"
  size = "sm",
  showLabel = true,
  isOwnContent = false,
  onVoteChange
}) => {
  const [showModal, setShowModal] = useState(false);
  const [votes, setVotes] = useState({});
  const [userVoted, setUserVoted] = useState([]);
  const [totalVotes, setTotalVotes] = useState(0);
  const [loading, setLoading] = useState(false);
  const [voting, setVoting] = useState(null);

  useEffect(() => {
    if (contentId) {
      fetchVotes();
    }
  }, [contentId]);

  const fetchVotes = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/awards/votes/${contentId}`,
        { withCredentials: true }
      );
      setVotes(response.data.vote_counts || {});
      setUserVoted(response.data.user_voted || []);
      setTotalVotes(response.data.total_votes || 0);
    } catch (error) {
      // User might not be logged in
      console.log('Could not fetch votes');
    }
  };

  const castVote = async (category) => {
    if (isOwnContent) {
      toast.error("You can't vote for your own content");
      return;
    }

    if (userVoted.includes(category)) {
      // Remove vote
      setVoting(category);
      try {
        await axios.delete(
          `${API_URL}/awards/vote/${contentId}/${category}`,
          { withCredentials: true }
        );
        setUserVoted(prev => prev.filter(c => c !== category));
        setVotes(prev => ({ ...prev, [category]: (prev[category] || 1) - 1 }));
        setTotalVotes(prev => prev - 1);
        toast.success('Vote removed');
        onVoteChange?.();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to remove vote');
      } finally {
        setVoting(null);
      }
    } else {
      // Cast vote
      setVoting(category);
      try {
        await axios.post(
          `${API_URL}/awards/vote`,
          {
            content_id: contentId,
            content_type: contentType,
            category: category
          },
          { withCredentials: true }
        );
        setUserVoted(prev => [...prev, category]);
        setVotes(prev => ({ ...prev, [category]: (prev[category] || 0) + 1 }));
        setTotalVotes(prev => prev + 1);
        toast.success(`Voted for ${AWARD_CATEGORIES[category].name}!`);
        onVoteChange?.();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to vote');
      } finally {
        setVoting(null);
      }
    }
  };

  const buttonSizes = {
    xs: 'h-6 px-2 text-xs',
    sm: 'h-8 px-3 text-sm',
    md: 'h-10 px-4 text-base'
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowModal(true)}
        className={`${buttonSizes[size]} rounded-full flex items-center gap-1.5 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 ${
          totalVotes > 0 ? 'text-yellow-600' : 'text-gray-500'
        }`}
        data-testid={`vote-for-btn-${contentId}`}
      >
        <Trophy className={`${size === 'xs' ? 'w-3 h-3' : size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'}`} />
        {showLabel && <span>Vote</span>}
        {totalVotes > 0 && (
          <span className="ml-1 px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full text-xs font-semibold">
            {totalVotes}
          </span>
        )}
      </Button>

      {/* Voting Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-md p-6 shadow-2xl"
              data-testid="vote-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Vote for Awards</h2>
                    <p className="text-sm text-gray-500">Select award categories</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {isOwnContent && (
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl text-sm text-yellow-700 dark:text-yellow-300">
                  You cannot vote for your own content
                </div>
              )}

              <div className="space-y-2">
                {Object.entries(AWARD_CATEGORIES).map(([key, category]) => {
                  const Icon = category.icon;
                  const hasVoted = userVoted.includes(key);
                  const voteCount = votes[key] || 0;
                  const isVoting = voting === key;

                  return (
                    <button
                      key={key}
                      onClick={() => castVote(key)}
                      disabled={isOwnContent || isVoting}
                      className={`w-full p-3 rounded-xl border-2 transition-all flex items-center justify-between ${
                        hasVoted
                          ? 'border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      } ${isOwnContent ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      data-testid={`vote-category-${key}`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${category.color}20` }}
                        >
                          <Icon className="w-5 h-5" style={{ color: category.color }} />
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{category.emoji}</span>
                            <span className="font-semibold text-gray-900 dark:text-white text-sm">
                              {category.name}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">{category.description}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {voteCount > 0 && (
                          <span className="text-sm font-semibold text-gray-500">
                            {voteCount}
                          </span>
                        )}
                        {isVoting ? (
                          <div className="w-6 h-6 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
                        ) : hasVoted ? (
                          <div className="w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        ) : (
                          <div className="w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-center text-sm text-gray-500">
                  Votes contribute to monthly & yearly awards 🏆
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default VoteForButton;
